import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import libphonenumber from 'google-libphonenumber';
import { Form, Input, Button, Row, Col, Select, notification } from 'antd';
import { FormattedMessage } from "react-intl";
import countryList from 'react-select-country-list'
import { cc, cf, cw, hp } from "../../utils";

import AuthHeader from './Header';

const InvitationSignup = (props) => {
    const { signIn, intl, _utils } = props;

    const [form] = Form.useForm();
    const recaptchaRef = useRef(null);

    const [country, setCountry] = useState({
        location: {},
        country_name: 'Ghana',
        country_code: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [, setRecaptcha] = useState(null);
    const [screen] = useState(1);

    const token = window.location.pathname.split('/')[3];
    const countries = countryList().getData();

    useEffect(() => {
        ipinfo();
    }, []);

    const ipinfo = () => {
        axios({
            method: 'POST',
            url: `${hp.getApiBaseUrl()}/ipinfo`,
            json: true
        }).then((res) => {
            if (res.data.country_name) {
                setCountry(res.data);
            }
        }).catch(() => {});
    };

    const onChangeRecap = value => {
        setRecaptcha(value);
    };

    const asyncAvailable = async (name, value) => {
        return await axios({
            method: 'GET',
            url: `${hp.getApiBaseUrl()}/auth/validations/${name}s/${value}`,
            json: false
        }).then((res) => {
            if (res.data.statusCode === 200) {
                return res.data.data.userExists;
            } else {
                return false;
            }
        }).catch(() => false);
    };

    const chheckEmailAddress = async (_, value) => {
        if (!value) {
            return Promise.resolve();
        }
        const checker = await asyncAvailable('email', value);
        if (checker === true) {
            return Promise.reject(`${value} already exists`);
        }
        return Promise.resolve();
    };

    const checkNumber = phone => {
        try {
            return libphonenumber.PhoneNumberUtil.getInstance().isPossibleNumber(
                libphonenumber.PhoneNumberUtil.getInstance().parse(phone, '')
            );
        } catch (error) {
            return false;
        }
    };

    const isPhoneNumber = phone => {
        try {
            return libphonenumber.PhoneNumberUtil.getInstance().isValidNumber(
                libphonenumber.PhoneNumberUtil.getInstance().parse(phone, '')
            );
        } catch (error) {
            return false;
        }
    };

    const checkPhoneNumber = async (_, value) => {
        if (!value) {
            return Promise.resolve();
        }
        const prefix = form.getFieldValue('prefix');
        const num = isPhoneNumber(`${prefix}${value}`);
        const state = checkNumber(`${prefix}${value}`);

        if (!num || !state) {
            return Promise.reject(intl.formatMessage(cw.formErrorMsgNotValidPhoneNumber));
        }

        const newPhone = `${prefix}${value.replace(/^0/, '')}`;
        const checker = await asyncAvailable('msisdn', newPhone.trim());
        if (checker === true) {
            return Promise.reject(`${value} already exists`);
        }
        return Promise.resolve();
    };

    const confirmPassword = (_, value) => {
        if (!value || value === form.getFieldValue('password')) {
            return Promise.resolve();
        }
        return Promise.reject(intl.formatMessage(cw.formErrorMsgMismatchPassword));
    };

    const _signup = (v) => {
        const { lang, inv_account } = _utils;
        const newPhone = `${v.prefix}${v.msisdn.replace(/^0/, '')}`;
        setSubmitting(true);
        axios({
            method: 'POST',
            url: `${hp.getApiBaseUrl()}/invitations/${token}/confirmations`,
            json: false,
            data: {
                firstName: v.firstName,
                lastName: v.lastName,
                email: v.email.trim(),
                msisdn: newPhone.trim(),
                userCountry: v.country,
                password: v.password,
                preferences: { language: lang, active_account: inv_account }
            }
        }).then((res) => {
            setSubmitting(false);
            if (res.data.statusCode === 200) {
                signIn(intl, lang, { email: v.email.trim(), password: v.password }, () => {
                    hp.setCookie('active_account', JSON.stringify(inv_account));
                    window.location.assign(hp.getSuccessRedirect());
                });
            } else {
                notification.error({
                    message: intl.formatMessage(cw['Success']),
                    description: intl.formatMessage(cw.signUpFailed)
                });
            }
        }).catch(() => {
            setSubmitting(false);
            notification.error({
                message: intl.formatMessage(cw['Failed']),
                description: intl.formatMessage(cw.signUpFailed)
            });
        });
    };

    const initialPrefix = `+${
        country.location && country.location.calling_code
            ? country.location.calling_code
            : '233'
    }`;

    const prefixSelector = (
        <Form.Item name="prefix" noStyle initialValue={initialPrefix}>
            <Select size="large" placeholder="+233" showSearch style={{ width: 80 }}
                onChange={e => {
                    const found = cc.find(c => c.dialCode === e);
                    if (found) form.setFieldValue('country', found.name);
                }}
            >
                {cc.map(c => (
                    <Select.Option key={c.countryCode} value={c.dialCode}>
                        {c.dialCode}
                    </Select.Option>
                ))}
            </Select>
        </Form.Item>
    );

    return (
        <React.Fragment>
            <Form
                id="esoko__signin"
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={_signup}
            >
                <AuthHeader screen="signup" />

                {/* Personal details */}
                <Row gutter={16} className={screen === 0 ? 'hide' : ''}>
                    <Col span={24}>
                        <p><b><FormattedMessage id="Invite.Signup.InfoText" defaultMessage="Provide your details to create an account and complete the process to join this organization." /></b></p>
                    </Col>
                    <Col span={12}>
                        <div>
                            <Form.Item
                                colon={false}
                                name="firstName"
                                label={<FormattedMessage id="Label.FirstName" defaultMessage="First Name" />}
                                rules={[
                                    { required: true, message: <FormattedMessage id="Rules.FirstName.Required" defaultMessage="Please provide first name" /> }
                                ]}
                            >
                                <Input size="large" type="text" autoComplete="off" sso-id="signup-firstname" />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <Form.Item
                                colon={false}
                                name="lastName"
                                label={<FormattedMessage id="Label.LastName" defaultMessage="Last Name" />}
                                rules={[
                                    { required: true, message: <FormattedMessage id="Rules.LastName.Required" defaultMessage="Please provide last name" /> }
                                ]}
                            >
                                <Input size="large" type="text" autoComplete="off" sso-id="signup-lastname" />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            colon={false}
                            name="email"
                            label={<FormattedMessage id="Label.EmailAddress" defaultMessage="Email Address" />}
                            initialValue={_utils.inv_email}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.EmailAddress.Required" defaultMessage="Please provide email address" /> },
                                { type: 'email', message: <FormattedMessage id="Rules.EmailAddress.Invalid" defaultMessage="Please check provided email" /> },
                                { validator: chheckEmailAddress, validateTrigger: 'onBlur' }
                            ]}
                        >
                            <Input size="large" type="email" autoComplete="off" disabled sso-id="signup-email" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            colon={false}
                            name="msisdn"
                            label={<FormattedMessage id="Label.Phone Number" defaultMessage="PhoneNumber" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.PhoneNumber.Required" defaultMessage="Please provide phone number" /> },
                                { validator: checkPhoneNumber, validateTrigger: 'onBlur' }
                            ]}
                        >
                            <Input size="large" type="text" addonBefore={prefixSelector} autoComplete="off" sso-id="signup-msisdn" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            colon={false}
                            name="country"
                            label={<FormattedMessage id="Label.Country" defaultMessage="Country" />}
                            initialValue="Ghana"
                            rules={[
                                { required: true, message: <span /> }
                            ]}
                        >
                            <Select size="large" showSearch={true} sso-id="signup-userCountry">
                                {countries.map(cnt => (
                                    <Select.Option key={cnt.label} value={cnt.label}>{cnt.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            colon={false}
                            name="password"
                            label={<FormattedMessage id="Label.Password" defaultMessage="Password" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.Password.Required" defaultMessage="Please provide password" /> },
                                { min: 6, message: <FormattedMessage id="Rules.Password.Min6" defaultMessage="Password must be at least 6 characters" /> }
                            ]}
                        >
                            <Input.Password size="large" type="password" minLength="6" autoComplete="off" sso-id="signup-password" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            colon={false}
                            name="cpassword"
                            label={<FormattedMessage id="Label.ConfirmPassword" defaultMessage="ConfirmPassword" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.Password.Required" defaultMessage="Please provide password" /> },
                                { min: 6, message: <FormattedMessage id="Rules.Password.Min6" defaultMessage="Password must be at least 6 characters" /> },
                                { validator: confirmPassword }
                            ]}
                        >
                            <Input.Password size="large" type="password" minLength="6" autoComplete="off" sso-id="signup-confirmPassword" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <div className="recaptchaDiv">
                            <ReCAPTCHA data-size="compact" ref={recaptchaRef} size="invisible" sitekey={cf.REACT_RECAPTCHA_SITE_KEY} onChange={onChangeRecap} />
                        </div>
                    </Col>
                    <Col span={24}>
                        <Form.Item>
                            <Button className="submitBtn" block size="large" type="primary" htmlType="submit" loading={submitting} sso-id="signup-button">
                                <FormattedMessage id="auth_signup_get_start" defaultMessage="Sign up" />
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>

                <div className="formLinks">
                    <p style={{ textAlign: 'center' }}>
                        <span className="noAccountText"><FormattedMessage id="auth_signin_got_account" defaultMessage="Already have an account?" /></span>
                        <Link to={`/${_utils.lang || 'en'}/signin${window.location.search}`} sso-id="login-link"><FormattedMessage id="auth_signin_signin" defaultMessage="Sign in" /></Link>
                    </p>
                </div>
            </Form>
        </React.Fragment>
    );
};

export default InvitationSignup;